import { ContentExtrasBar } from '@/components/content/ContentExtrasBar';
import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { 
  Send, 
  CheckCircle,
  Sparkles,
  User,
  RefreshCw,
  Lightbulb,
  Loader2,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import ReactMarkdown from 'react-markdown';

interface LiaClassModalProps {
  isOpen: boolean;
  contentId?: string;
  onClose: () => void;
  lesson: {
    title: string;
    description: string;
    duration: string;
  };
  customPrompt?: string;
  onComplete: () => void;
  isCompleted: boolean;
}

interface Message {
  id: string;
  role: 'lia' | 'user';
  content: string;
}

function parseSteps(prompt?: string): string[] {
  if (!prompt) return [];
  return prompt
    .split('\n')
    .filter(l => /^\d+\.\s/.test(l.trim()))
    .map(l => l.trim());
}

function cleanMarkers(text: string): string {
  return text
    .replace(/\[PASO\s*\d+\s*\/\s*\d+\]/gi, '')
    .replace(/\[CLASE_COMPLETADA\]/gi, '')
    .trim();
}

function detectCurrentStep(text: string): number | null {
  const match = text.match(/\[PASO\s*(\d+)\s*\/\s*\d+\]/i);
  return match ? parseInt(match[1], 10) : null;
}

export function LiaClassModal({
  contentId,
  isOpen,
  onClose,
  lesson,
  customPrompt,
  onComplete,
  isCompleted
}: LiaClassModalProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [autoCompleted, setAutoCompleted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const steps = useMemo(() => parseSteps(customPrompt), [customPrompt]);
  const totalSteps = steps.length;
  const hasSteps = totalSteps > 0;

  // Auto-trigger first step on open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      if (hasSteps) {
        // Send first step context automatically
        setMessages([{
          id: 'loading',
          role: 'lia',
          content: '...',
        }]);
        triggerFirstStep();
      } else {
        setMessages([{
          id: '1',
          role: 'lia',
          content: `¡Hola! 👋 Soy LIA, tu asistente de aprendizaje. Hoy vamos a explorar juntos: **${lesson.title}**.\n\n${lesson.description}\n\n¿Estás listo/a para comenzar?`,
        }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const buildSystemInstructions = () => {
    if (!hasSteps) return '';
    return `[Instrucciones para el asistente: Esta clase interactiva tiene ${totalSteps} pasos. Los pasos son:\n${steps.join('\n')}\n\nReglas:\n- Ejecuta UN solo paso a la vez.\n- Al inicio de cada respuesta incluye exactamente el marcador [PASO X/${totalSteps}] donde X es el número del paso actual.\n- Después del marcador, desarrolla el contenido del paso: explica, da ejemplos y haz preguntas al participante.\n- Cuando el participante responda satisfactoriamente, avanza al siguiente paso.\n- Al completar el ÚLTIMO paso, incluye el marcador [CLASE_COMPLETADA] al final de tu respuesta.\n- Sé un tutor amigable, claro y práctico.]\n\n`;
  };

  const triggerFirstStep = async () => {
    setIsTyping(true);
    try {
      const contextMessage = `${buildSystemInstructions()}[Clase: "${lesson.title}". Descripción: ${lesson.description}. Duración: ${lesson.duration}.]\n\nComienza con el Paso 1.`;

      const { data, error } = await supabase.functions.invoke('lia-chat', {
        body: { session_id: null, message: contextMessage, context_type: 'lia_class' },
      });

      if (error) throw error;

      if (data.session_id) setSessionId(data.session_id);

      const rawResponse = data.response || 'Lo siento, no pude generar una respuesta.';
      const detectedStep = detectCurrentStep(rawResponse);
      if (detectedStep) setCurrentStep(detectedStep);

      const cleaned = cleanMarkers(rawResponse);

      setMessages([{
        id: `lia-${Date.now()}`,
        role: 'lia',
        content: cleaned,
      }]);
      setMessageCount(1);

      handleCompletionCheck(rawResponse);
    } catch {
      setMessages([{
        id: `lia-${Date.now()}`,
        role: 'lia',
        content: '⚠️ Hubo un error al conectar con LIA. Intenta de nuevo.',
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleCompletionCheck = (rawResponse: string) => {
    if (/\[CLASE_COMPLETADA\]/i.test(rawResponse) && !isCompleted && !autoCompleted) {
      setAutoCompleted(true);
      setTimeout(() => onComplete(), 1500);
    }
  };

  const sendToLia = async (text: string) => {
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const contextMessage = messageCount === 0
        ? hasSteps
          ? `${buildSystemInstructions()}[Clase: "${lesson.title}". Descripción: ${lesson.description}. Duración: ${lesson.duration}.]\n\n${text}`
          : customPrompt
            ? `[Instrucciones del asistente para esta clase: ${customPrompt}]\n\n[Clase: "${lesson.title}". Descripción: ${lesson.description}. Duración: ${lesson.duration}.]\n\n${text}`
            : `[Contexto: Estamos en una clase interactiva sobre "${lesson.title}". Descripción: ${lesson.description}. Duración estimada: ${lesson.duration}. Guía al usuario paso a paso como un tutor interactivo, haciendo preguntas y dando ejemplos prácticos.]\n\n${text}`
        : text;

      const { data, error } = await supabase.functions.invoke('lia-chat', {
        body: { session_id: sessionId, message: contextMessage, context_type: 'lia_class' },
      });

      if (error) throw error;

      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }

      const rawResponse = data.response || 'Lo siento, no pude generar una respuesta.';
      const detectedStep = detectCurrentStep(rawResponse);
      if (detectedStep) setCurrentStep(detectedStep);

      const cleaned = cleanMarkers(rawResponse);

      setMessages(prev => [...prev, {
        id: `lia-${Date.now()}`,
        role: 'lia',
        content: cleaned,
      }]);
      setMessageCount(prev => prev + 1);

      handleCompletionCheck(rawResponse);
    } catch {
      setMessages(prev => [...prev, {
        id: `lia-${Date.now()}`,
        role: 'lia',
        content: '⚠️ Hubo un error al conectar con LIA. Intenta de nuevo.',
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSendMessage = () => {
    if (!inputValue.trim() || isTyping) return;
    sendToLia(inputValue);
  };

  const handleRestart = () => {
    setMessages([]);
    setMessageCount(0);
    setSessionId(null);
    setCurrentStep(1);
    setAutoCompleted(false);
    // Re-trigger on next render via the useEffect
    setTimeout(() => {
      if (hasSteps) {
        setMessages([{ id: 'loading', role: 'lia', content: '...' }]);
        triggerFirstStep();
      } else {
        setMessages([{
          id: '1',
          role: 'lia',
          content: `¡Hola! 👋 Reiniciemos la clase sobre **${lesson.title}**. ¿Estás listo/a?`,
        }]);
      }
    }, 50);
  };

  const progressPercent = hasSteps ? Math.round(((currentStep - 1) / totalSteps) * 100) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(
        "p-0 flex flex-col overflow-hidden bg-gradient-to-b from-card to-card/95",
        isFullscreen
          ? "max-w-[100vw] w-screen h-screen max-h-screen rounded-none"
          : "max-w-2xl h-[85vh]"
      )}>
        <DialogHeader className="p-4 pb-3 border-b border-border bg-gradient-to-r from-primary/10 to-secondary/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary via-secondary to-accent flex items-center justify-center shadow-lg">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-success border-2 border-card" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                  Clase con LIA
                  <Badge variant="outline" className="text-xs font-normal">En vivo</Badge>
                </DialogTitle>
                <p className="text-sm text-muted-foreground">{lesson.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={handleRestart} className="gap-1">
                <RefreshCw className="w-4 h-4" /> Reiniciar
              </Button>
              {(isCompleted || autoCompleted) && (
                <Badge className="bg-success text-white gap-1">
                  <CheckCircle className="w-3 h-3" /> Completado
                </Badge>
              )}
              <button
                onClick={() => setIsFullscreen(f => !f)}
                className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
                title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4 text-muted-foreground" /> : <Maximize2 className="w-4 h-4 text-muted-foreground" />}
              </button>
            </div>
          </div>
          {hasSteps && (
            <div className="mt-3 space-y-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Paso {currentStep} de {totalSteps}</span>
                <span>{autoCompleted ? '100' : progressPercent}%</span>
              </div>
              <Progress value={autoCompleted ? 100 : progressPercent} className="h-1.5" />
            </div>
          )}
        </DialogHeader>
        {contentId && <ContentExtrasBar contentId={contentId} description={lesson.description} />}

        <div className="flex-1 overflow-y-auto p-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.filter(m => m.id !== 'loading' || isTyping).map((message) => (
              <div
                key={message.id}
                className={cn("flex gap-3", message.role === 'user' && "justify-end")}
              >
                {message.role === 'lia' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className={cn(
                  "max-w-[80%] rounded-2xl px-4 py-3",
                  message.role === 'lia'
                    ? "bg-muted text-foreground rounded-tl-none"
                    : "bg-primary text-primary-foreground rounded-tr-none"
                )}>
                  {message.role === 'lia' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-2 [&>ul]:mb-2">
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  )}
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="bg-muted rounded-2xl rounded-tl-none px-4 py-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">LIA está pensando...</span>
                </div>
              </div>
            )}

            {autoCompleted && (
              <div className="flex justify-center">
                <Badge className="bg-success/10 text-success border-success/30 gap-1 px-4 py-2 text-sm">
                  <CheckCircle className="w-4 h-4" /> ¡Clase completada! 🎉
                </Badge>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        <div className="p-4 border-t border-border bg-card">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-secondary" />
            <p className="text-xs text-muted-foreground">
              {hasSteps
                ? `Responde a LIA para avanzar al siguiente paso (${currentStep}/${totalSteps})`
                : 'Escribe tu respuesta o pregunta para continuar la clase'}
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Escribe tu respuesta..."
              className="flex-1"
              disabled={isTyping}
            />
            <Button onClick={handleSendMessage} disabled={!inputValue.trim() || isTyping}>
              <Send className="w-4 h-4" />
            </Button>
          </div>

          {/* Manual complete button — only for free-form prompts (no steps) */}
          {!hasSteps && !isCompleted && messageCount >= 3 && (
            <div className="mt-3 flex justify-center">
              <Button onClick={onComplete} className="gap-2">
                <CheckCircle className="w-4 h-4" />
                Marcar clase como completada
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
